/**
 * Phase 14c — operator self-service /me page.
 *
 * Single-scroll layout (14c-2) with five section cards:
 *   1. Profile (displayName editor) — PATCH /me/operator/profile
 *   2. Password — change (POST /auth/password), or, for a passwordless
 *      account, "email me a link to set one" (POST /auth/password-reset/request)
 *   3. TOTP (re-enroll modal; Disable button hidden for operators per 14c-5)
 *   4. Notification preferences (subscription toggles) — PATCH /me/operator/notification-preferences
 *   5. Dashboard preferences (metric catalog checkboxes) — PATCH /me/operator/preferences
 *
 * Closes the B3 backlog item (14b-7 lock). The Configure UI for dashboard
 * preferences was deferred from Phase 14a-3 to here per the 14a-3 lock.
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Field,
  Input,
  Button,
  Alert,
} from '@truecare/ui';
import type {
  MetricCatalogResponse,
  OperatorNotificationPreferencesResponse,
  OperatorPreferenceResponse,
  OperatorProfileResponse,
} from '@truecare/schemas';
import { ApiError, unwrap } from '@truecare/api-client';
import { api } from '../api';
import { adminBeforeLoad } from '../lib/admin-before-load';
import { AdminLayout } from '../components/AdminLayout';
import { TotpReEnrollModal } from '../components/TotpReEnrollModal';

export const Route = createFileRoute('/me')({
  beforeLoad: () => adminBeforeLoad('/me'),
  component: MePage,
});

function MePage() {
  return (
    <AdminLayout>
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-forest md:text-4xl">My profile</h1>
        <p className="font-body text-sm text-ink-muted">
          Manage your display name, password, two-factor authentication, and
          notification and dashboard preferences.
        </p>
      </header>
      <ProfileSection />
      <PasswordSection />
      <TotpSection />
      <NotificationPreferencesSection />
      <DashboardPreferencesSection />
    </AdminLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Profile (displayName)
// ─────────────────────────────────────────────────────────────────────

function ProfileSection() {
  const queryClient = useQueryClient();
  const profile = useQuery<OperatorProfileResponse>({
    queryKey: ['operator', 'profile'],
    queryFn: async () =>
      (await unwrap(api.GET('/api/v1/me/operator/profile' as never))) as unknown as OperatorProfileResponse,
  });

  const [displayName, setDisplayName] = useState('');
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (profile.data && !touched) {
      setDisplayName(profile.data.displayName ?? '');
    }
  }, [profile.data, touched]);

  const mutation = useMutation({
    mutationFn: async (value: string) =>
      (await unwrap(
        api.PATCH('/api/v1/me/operator/profile' as never, {
          body: { displayName: value },
        } as never),
      )) as unknown as OperatorProfileResponse,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['operator', 'profile'] });
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      setTouched(false);
    },
  });

  const trimmed = displayName.trim();
  const isDirty = trimmed !== (profile.data?.displayName ?? '');
  const isValid = trimmed.length > 0 && trimmed.length <= 100;

  return (
    <Card padding="md" className="mt-6" data-testid="me-profile-card">
      <CardHeader title="Profile" description="Display name shown in the admin header." />
      <Field label="Email" id="me-email">
        <Input id="me-email" value={profile.data?.email ?? ''} disabled />
      </Field>
      <Field label="Display name" id="me-display-name" className="mt-4">
        <Input
          id="me-display-name"
          data-testid="me-display-name-input"
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            setTouched(true);
          }}
          maxLength={100}
        />
      </Field>
      <div className="mt-4 flex items-center gap-3">
        <Button
          data-testid="me-display-name-save"
          onClick={() => mutation.mutate(trimmed)}
          disabled={!isDirty || !isValid || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {mutation.isSuccess && !isDirty && (
          <span className="text-sm text-success" data-testid="me-display-name-saved">
            Saved.
          </span>
        )}
      </div>
      {mutation.isError && (
        <Alert tone="danger" className="mt-3" data-testid="me-display-name-error">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'Could not save display name.'}
        </Alert>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Password
// ─────────────────────────────────────────────────────────────────────

function PasswordSection() {
  const queryClient = useQueryClient();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Shared query key with ProfileSection → one fetch. `hasPassword` picks the
  // card: change-password, or the set-a-password-by-email card.
  const profile = useQuery<OperatorProfileResponse>({
    queryKey: ['operator', 'profile'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/me/operator/profile' as never),
      )) as unknown as OperatorProfileResponse,
  });
  const mutation = useMutation({
    mutationFn: async () => {
      await unwrap(
        api.POST('/api/v1/auth/password' as never, {
          body: { currentPassword, newPassword },
        } as never),
      );
    },
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSubmitted(true);
      void queryClient.invalidateQueries({ queryKey: ['operator', 'profile'] });
    },
  });

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  // Backend NewPasswordSchema requires min(12) — match here so the form
  // doesn't submit a guaranteed-400. Backend error text would be
  // "Password must be at least 12 characters."
  const canSubmit =
    currentPassword.length > 0 && newPassword.length >= 12 && passwordsMatch;

  // Render nothing until we know (avoids flashing the wrong card).
  if (!profile.isSuccess) return null;
  // Passwordless (magic-link-only) accounts set a FIRST password via the
  // reset-email flow, not an in-page form (that was confusing and hit a buggy
  // first-time-set path) — offer a one-click "email me a link" instead.
  if (!profile.data.hasPassword) {
    return <SetPasswordByEmailCard email={profile.data.email} />;
  }

  return (
    <Card padding="md" className="mt-6" data-testid="me-password-card">
      <CardHeader
        title="Password"
        description="≥12 characters; current password required to confirm."
      />
      <Field label="Current password" id="me-current-password">
        <Input
          id="me-current-password"
          data-testid="me-current-password"
          type="password"
          value={currentPassword}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
            setSubmitted(false);
          }}
          autoComplete="current-password"
        />
      </Field>
      <Field label="New password" id="me-new-password" className="mt-4">
        <Input
          id="me-new-password"
          data-testid="me-new-password"
          type="password"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            setSubmitted(false);
          }}
          autoComplete="new-password"
          minLength={12}
        />
      </Field>
      <Field label="Confirm new password" id="me-confirm-password" className="mt-4">
        <Input
          id="me-confirm-password"
          data-testid="me-confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setSubmitted(false);
          }}
          autoComplete="new-password"
          minLength={12}
        />
      </Field>
      {confirmPassword.length > 0 && !passwordsMatch && (
        <Alert tone="warning" className="mt-3" data-testid="me-password-mismatch">
          New passwords don't match.
        </Alert>
      )}
      <div className="mt-4 flex items-center gap-3">
        <Button
          data-testid="me-password-save"
          onClick={() => mutation.mutate()}
          disabled={!canSubmit || mutation.isPending}
        >
          {mutation.isPending ? 'Updating…' : 'Update password'}
        </Button>
        {submitted && (
          <span className="text-sm text-success" data-testid="me-password-saved">
            Password updated.
          </span>
        )}
      </div>
      {mutation.isError && (
        <Alert tone="danger" className="mt-3" data-testid="me-password-error">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'Could not update password.'}
        </Alert>
      )}
    </Card>
  );
}

// Passwordless account: one click emails a set-password link (the standard
// reset flow — single-use, expiring). The link page sets the password, signs
// out other sessions and starts a fresh one; back here the profile refetches
// with hasPassword=true, so the change-password card shows instead.
function SetPasswordByEmailCard({ email }: { email: string }) {
  const mutation = useMutation({
    mutationFn: async () => {
      await unwrap(
        api.POST('/api/v1/auth/password-reset/request' as never, {
          body: { email },
        } as never),
      );
    },
  });

  return (
    <Card padding="md" className="mt-6" data-testid="me-set-password-card">
      <CardHeader
        title="Set a password"
        description="You sign in with emailed links. To also sign in with a password, we'll email you a link to set one."
      />
      {mutation.isSuccess ? (
        <Alert tone="success" className="mt-2" data-testid="me-set-password-sent">
          Check {email} for a link to set your password.
        </Alert>
      ) : (
        <div className="mt-2">
          <Button
            data-testid="me-set-password-send"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Sending…' : 'Email me a link'}
          </Button>
        </div>
      )}
      {mutation.isError && (
        <Alert tone="danger" className="mt-3" data-testid="me-set-password-error">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'Could not send the email. Try again in a minute.'}
        </Alert>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// TOTP
// ─────────────────────────────────────────────────────────────────────

function TotpSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const auth = useQuery<{ totpEnrolled: boolean } | null>({
    queryKey: ['auth', 'me'],
    queryFn: async () =>
      (await unwrap(api.GET('/api/v1/auth/me' as never))) as unknown as {
        totpEnrolled: boolean;
      } | null,
  });
  const isEnrolled = auth.data?.totpEnrolled ?? false;

  return (
    <Card padding="md" className="mt-6" data-testid="me-totp-card">
      <CardHeader
        title="Two-factor authentication (TOTP)"
        description={
          isEnrolled
            ? 'TOTP is enrolled. Re-enroll to switch authenticator devices.'
            : 'TOTP is not yet enrolled.'
        }
      />
      <div className="flex gap-3">
        <Button
          data-testid="me-totp-re-enroll"
          onClick={() => setModalOpen(true)}
          variant={isEnrolled ? 'secondary' : 'primary'}
        >
          {isEnrolled ? 'Re-enroll TOTP' : 'Enroll TOTP'}
        </Button>
        {/* Phase 14c-5: Disable button is suppressed for operators —
            backend refuses operator disable with reason
            totp_disable_forbidden_for_operator; matching the backend
            shape with a hidden button avoids a guaranteed-failure click. */}
      </div>
      {modalOpen && (
        <TotpReEnrollModal
          isReEnrollment={isEnrolled}
          onClose={() => setModalOpen(false)}
        />
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Notification preferences
// ─────────────────────────────────────────────────────────────────────

function NotificationPreferencesSection() {
  const queryClient = useQueryClient();
  const prefs = useQuery<OperatorNotificationPreferencesResponse>({
    queryKey: ['operator', 'notification-preferences'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/me/operator/notification-preferences' as never),
      )) as unknown as OperatorNotificationPreferencesResponse,
  });

  // Local draft state — checkboxes update locally, save button commits.
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (prefs.data) {
      const next: Record<string, boolean> = {};
      for (const p of prefs.data.preferences) next[p.templateCodeName] = p.subscribed;
      setDraft(next);
    }
  }, [prefs.data]);

  const isDirty =
    prefs.data?.preferences.some((p) => draft[p.templateCodeName] !== p.subscribed) ?? false;

  const mutation = useMutation({
    mutationFn: async () => {
      const updates = prefs.data?.preferences
        .filter((p) => draft[p.templateCodeName] !== p.subscribed)
        .map((p) => ({
          templateCodeName: p.templateCodeName,
          subscribed: draft[p.templateCodeName] ?? p.subscribed,
        })) ?? [];
      if (updates.length === 0) return;
      await unwrap(
        api.PATCH('/api/v1/me/operator/notification-preferences' as never, {
          body: { updates },
        } as never),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['operator', 'notification-preferences'] });
    },
  });

  return (
    <Card padding="md" className="mt-6" data-testid="me-notification-prefs-card">
      <CardHeader
        title="Notification preferences"
        description="Which operator-facing notifications you receive. New operator-facing templates land here as they ship."
      />
      <ul className="space-y-3">
        {prefs.data?.preferences.map((p) => (
          <li key={p.templateCodeName} className="flex items-start gap-3">
            <input
              type="checkbox"
              id={`me-notif-${p.templateCodeName}`}
              data-testid={`me-notif-${p.templateCodeName}`}
              className="mt-1 h-4 w-4 accent-forest"
              checked={draft[p.templateCodeName] ?? false}
              onChange={(e) =>
                setDraft((d) => ({ ...d, [p.templateCodeName]: e.target.checked }))
              }
            />
            <label htmlFor={`me-notif-${p.templateCodeName}`} className="flex-1">
              <div className="font-medium text-ink">{p.label}</div>
              <div className="text-sm text-ink-muted">{p.description}</div>
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center gap-3">
        <Button
          data-testid="me-notif-save"
          onClick={() => mutation.mutate()}
          disabled={!isDirty || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {mutation.isSuccess && !isDirty && (
          <span className="text-sm text-success" data-testid="me-notif-saved">
            Saved.
          </span>
        )}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Dashboard preferences
// ─────────────────────────────────────────────────────────────────────

function DashboardPreferencesSection() {
  const queryClient = useQueryClient();
  const catalog = useQuery<MetricCatalogResponse>({
    queryKey: ['admin', 'dashboard', 'metrics'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/dashboard/metrics' as never),
      )) as unknown as MetricCatalogResponse,
  });
  const prefs = useQuery<OperatorPreferenceResponse>({
    queryKey: ['operator', 'preferences'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/me/operator/preferences' as never),
      )) as unknown as OperatorPreferenceResponse,
  });

  const [draft, setDraft] = useState<string[]>([]);
  useEffect(() => {
    if (prefs.data) setDraft([...prefs.data.dashboardMetricIds]);
  }, [prefs.data]);

  const isDirty =
    prefs.data &&
    (draft.length !== prefs.data.dashboardMetricIds.length ||
      draft.some((id, i) => id !== prefs.data!.dashboardMetricIds[i]));

  const mutation = useMutation({
    mutationFn: async () => {
      await unwrap(
        api.PATCH('/api/v1/me/operator/preferences' as never, {
          body: { dashboardMetricIds: draft },
        } as never),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['operator', 'preferences'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
  });

  return (
    <Card padding="md" className="mt-6" data-testid="me-dashboard-prefs-card">
      <CardHeader
        title="Dashboard preferences"
        description="Which metric cards appear on your dashboard. Order is preserved (no drag-to-reorder at v1)."
      />
      <ul className="space-y-3">
        {catalog.data?.items.map((m) => {
          const selected = draft.includes(m.id);
          return (
            <li key={m.id} className="flex items-start gap-3">
              <input
                type="checkbox"
                id={`me-dash-${m.id}`}
                data-testid={`me-dash-${m.id}`}
                className="mt-1 h-4 w-4 accent-forest"
                checked={selected}
                onChange={(e) =>
                  setDraft((d) =>
                    e.target.checked ? [...d, m.id] : d.filter((id) => id !== m.id),
                  )
                }
              />
              <label htmlFor={`me-dash-${m.id}`} className="flex-1">
                <div className="font-medium text-ink">{m.label}</div>
                <div className="text-sm text-ink-muted">{m.description}</div>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex items-center gap-3">
        <Button
          data-testid="me-dash-save"
          onClick={() => mutation.mutate()}
          disabled={!isDirty || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {mutation.isSuccess && !isDirty && (
          <span className="text-sm text-success" data-testid="me-dash-saved">
            Saved.
          </span>
        )}
      </div>
    </Card>
  );
}
